import { useEffect, useRef, useState } from "react";
import type { DashboardData, Owner } from "../types";
import { api } from "../lib/api";
import { normalizeDashboardData, readOfflineDashboard, saveOfflineDashboard } from "../lib/offline";
import { satisfies, widerNeed, needWindow, type Need } from "../lib/coverage";

/**
 * The household's data: loaded once someone is signed in, and reloaded after every change.
 *
 * Only as much of the ledger as `need` asks for is fetched. When a screen needs more than is
 * held, the fuller payload is fetched while the current one stays on screen. Additional detail
 * can widen the current window; changing the selected month resets it.
 * A single old entry can be pinned instead (`pin`): it is asked for by id and kept in every
 * reload, so opening one receipt's entry does not pull in every month back to it.
 */
export function useDashboardData({ profile, setProfile, setError, need, scopeKey }: {
  profile: Owner | null;
  setProfile: (profile: Owner | null) => void;
  setError: (message: string) => void;
  need: Need;
  /** Reset the base window on navigation even when the selected month stays the same. */
  scopeKey?: string;
}) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [offlineSince, setOfflineSince] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const wanted = useRef<Need>(need);
  const held = useRef<DashboardData | null>(null);
  const pinned = useRef(new Set<string>());
  const latest = useRef(0);
  const lastProfile = useRef<Owner | null>(null);
  const show = (next: DashboardData | null | ((current: DashboardData | null) => DashboardData | null)) => {
    const d = typeof next === "function" ? next(held.current) : next;
    held.current = d;
    setData(d);
  };

  async function refresh(force = false) {
    const request = ++latest.current;
    const window = needWindow(wanted.current);
    const query = [
      force ? "refresh=1" : "",
      window ? `since=${window.since}` : "",
      window?.through ? `through=${window.through}` : "",
      window && pinned.current.size ? `include=${[...pinned.current].join(",")}` : "",
    ].filter(Boolean).join("&");
    setRefreshing(true);
    setError("");
    try {
      const d = normalizeDashboardData(await api<DashboardData>(query ? `data?${query}` : "data"));
      // A newer request is already on its way and will say what is current.
      if (request !== latest.current) return d;
      saveOfflineDashboard(d);
      show(d);
      setOfflineSince(null);
      return d;
    } catch (e) {
      if (request !== latest.current) return;
      if ((e as { status?: number }).status === 401) {
        setProfile(null);
        show(null);
        setOfflineSince(null);
      } else {
        const cached = readOfflineDashboard();
        if (cached) {
          if (!held.current) show(normalizeDashboardData(cached.data));
          setOfflineSince(cached.savedAt);
        }
        setError((e as Error).message);
      }
    } finally {
      if (request === latest.current) setRefreshing(false);
    }
  }

  /** Makes sure at least `wider` is held, fetching it if not. Resolves with the payload once it is. */
  function ensure(wider: Need): Promise<DashboardData | undefined> {
    wanted.current = widerNeed(wanted.current, wider);
    if (!held.current || !satisfies(held.current, wanted.current)) return refresh();
    return Promise.resolve(held.current);
  }

  /** Makes sure these entries are held whatever their date. Each id is asked for once. */
  function pin(ids: string[]): Promise<DashboardData | undefined> {
    const fresh = ids.filter((id) => !pinned.current.has(id));
    fresh.forEach((id) => pinned.current.add(id));
    const missing = fresh.filter((id) => !held.current?.transactions.some((t) => t.id === id));
    return held.current && !missing.length ? Promise.resolve(held.current) : refresh();
  }

  useEffect(() => {
    if (!profile) {
      wanted.current = need;
      lastProfile.current = null;
      return;
    }
    const switched = lastProfile.current !== profile;
    lastProfile.current = profile;
    if (switched) {
      wanted.current = widerNeed(wanted.current, need);
      void refresh();
    } else if (need.includes("..")) {
      // Changing month replaces the base window. Browsing far back must not
      // permanently turn every later save into a multi-year ledger reload.
      wanted.current = need;
      if (!held.current || !satisfies(held.current, need)) void refresh();
    } else ensure(need);
  // Runs on sign-in and when a screen asks for more; `refresh` is recreated each render and must not retrigger it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, need, scopeKey]);

  return { data, setData: show, refreshing, refresh, ensure, pin, offlineSince };
}
